import useGetRequest from 'hooks/useGetRequest';
import { Course } from 'models/Course';
import { CourseScheduleOverview } from 'models/CourseScheduleOverview';
import { UserModel } from 'models/User';
import React, { useContext } from 'react';
import { AuthContext } from './AuthContext';
import { Organisation } from 'models/Organisation';

type OADashboardContextValue = {
    organisation: Organisation | undefined;
    refetchOrganisation: () => Promise<void>;
    courses: Course[] | undefined;
    refetchCourses: () => Promise<void>;
    liveCourses: CourseScheduleOverview[] | undefined;
    refetechLiveCourses: () => Promise<void>;
    users: UserModel[] | undefined;
    refetchUsers: () => Promise<void>;
    errors: boolean;
};

export const OADashboardContext = React.createContext<OADashboardContextValue>({
    organisation: undefined,
    refetchOrganisation: async () => {},
    courses: undefined,
    refetchCourses: async () => {},
    liveCourses: undefined,
    refetechLiveCourses: async () => {},
    users: undefined,
    refetchUsers: async () => {},
    errors: false,
});

export function OADashboardProvider(props: { children: React.ReactNode }): JSX.Element {
    const { userData } = useContext(AuthContext);
    const organisationId = userData.organisation!.id;

    const {
        data: organisation,
        refetchData: refetchOrganisation,
        errors: organisationErrors,
    } = useGetRequest<Organisation>(`organisations/${organisationId}`, !organisationId);

    const {
        data: courses,
        refetchData: refetchCourses,
        errors: coursesErrors,
    } = useGetRequest<Course[]>(`organisations/${organisationId}/courses`, !organisationId);

    const {
        data: liveCourses,
        refetchData: refetechLiveCourses,
        errors: liveCoursesErrors,
    } = useGetRequest<CourseScheduleOverview[]>(`organisations/${organisationId}/scheduled-courses`, !organisationId);

    const {
        data: users,
        refetchData: refetchUsers,
        errors: usersErrors,
    } = useGetRequest<UserModel[]>(`organisations/users`, !organisationId);

    let errors: boolean = false;
    if (
        (organisationErrors && organisationErrors.length) ||
        (coursesErrors && coursesErrors.length) ||
        (liveCoursesErrors && liveCoursesErrors.length) ||
        (usersErrors && usersErrors.length)
    ) {
        errors = true;
    }

    if (!organisation || !courses || !liveCourses || !users) return <></>;

    return (
        <OADashboardContext.Provider
            value={{
                organisation: organisation,
                refetchOrganisation: refetchOrganisation,
                courses: courses,
                refetchCourses: refetchCourses,
                liveCourses: liveCourses,
                refetechLiveCourses: refetechLiveCourses,
                users: users,
                refetchUsers: refetchUsers,
                errors: errors,
            }}
        >
            {props.children}
        </OADashboardContext.Provider>
    );
}
